import React, { useState, useMemo } from "react";
import { FormControl, InputLabel, Select, MenuItem, Grid, Typography, TextField, InputAdornment, Box, Chip, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { BotMessageType, BotStatus, ERoles } from "../../config";
import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";

const SearchableBotDropdownForTemplate = ({
    value,
    onChange,
    onBlur,
    disabled = false,
    error,
    helperText,
    botList = [],
    label = "Select Bot",
    labelId = "select-bot-label",
    id = "select-bot",
    user,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [open, setOpen] = useState(false);

    // Filter bots by search term
    const filteredBots = useMemo(() => {
        if (!searchTerm) return botList;
        return botList.filter(bot => bot.botName?.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [botList, searchTerm]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setSearchTerm("");
    };

    return (
        <FormControl fullWidth>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                labelId={labelId}
                id={id}
                value={value ?? "All"}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                error={Boolean(error)}
                label={label}
                fullWidth
                MenuProps={{
                    PaperProps: {
                        sx: {
                            overflow: "scroll",
                            maxHeight: "50vh",
                            boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        position: "sticky",
                        top: 0,
                        backgroundColor: "white",
                        zIndex: 1,
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Search bots..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#f5f5f5",
                            },
                        }}
                    />
                </Box>

                {!searchTerm && (
                    <MenuItem value="All">
                        <Grid
                            container
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <Grid item md={6} display="flex" flexDirection="column">
                                <Typography sx={{ fontSize: "0.9rem", fontWeight: "400" }}>All</Typography>
                            </Grid>
                        </Grid>
                    </MenuItem>
                )}

                {filteredBots.length > 0 ? (
                    filteredBots.map((bot, index) => (
                        <MenuItem key={index} value={bot.botName}>
                            <Grid
                                container
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                {/* <Grid item md={6} display="flex" flexDirection="column">
                                    <Typography sx={{ fontSize: "0.9rem", fontWeight: "400" }}>{bot.botName}</Typography>
                                    {user && user.roleName?.toLowerCase() === ERoles.SUPER_ADMIN && (
                                        <Typography variant="caption">{bot.clientName}</Typography>
                                    )}
                                </Grid> */}
                                <Grid item md={6} display="flex" flexDirection="column">
                                    <Tooltip title={bot.botName.length > 30 ? bot.botName : ""} arrow>
                                        <Typography
                                            sx={{
                                                fontSize: "0.9rem",
                                                fontWeight: "400",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                maxWidth: "250px",
                                                cursor: bot.botName.length > 30 ? "pointer" : "default",
                                            }}
                                        >
                                            {bot.botName}
                                        </Typography>
                                    </Tooltip>

                                    {user && user.roleName?.toLowerCase() === ERoles.SUPER_ADMIN && (
                                        <Tooltip title={bot.clientName.length > 30 ? bot.clientName : ""} arrow>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "250px",
                                                    cursor: bot.botName.length > 30 ? "pointer" : "default",
                                                }}
                                            >
                                                {bot.clientName}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Typography variant="caption">
                                        Status : &nbsp;
                                        {bot.status === BotStatus.New ? (
                                            <Chip
                                                variant="outlined"
                                                className="dropdownChip"
                                                label={bot.status.replaceAll("_", " ").toUpperCase()}
                                                color="primary"
                                                size="small"
                                            />
                                        ) : bot.status === BotStatus.Development ? (
                                            <Chip
                                                variant="outlined"
                                                className="dropdownChip"
                                                label={bot.status.replaceAll("_", " ").toUpperCase()}
                                                color="info"
                                                size="small"
                                            />
                                        ) : bot.status === BotStatus.VerificationInProcess ? (
                                            <Chip
                                                variant="outlined"
                                                className="dropdownChip"
                                                label={bot.status.replaceAll("_", " ").toUpperCase()}
                                                color="warning"
                                                size="small"
                                            />
                                        ) : bot.status === BotStatus.Verified ? (
                                            <Chip
                                                variant="outlined"
                                                className="dropdownChip"
                                                label={bot.status.replaceAll("_", " ").toUpperCase()}
                                                color="success"
                                                size="small"
                                            />
                                        ) : bot.status === BotStatus.Launched ? (
                                            <Chip
                                                variant="outlined"
                                                className="dropdownChip"
                                                label={bot.status.replaceAll("_", " ").toUpperCase()}
                                                color="success"
                                                size="small"
                                            />
                                        ) : (
                                            <Chip
                                                variant="outlined"
                                                className="dropdownChip"
                                                label={bot.status.replaceAll("_", " ").toUpperCase()}
                                                color="warning"
                                                size="small"
                                            />
                                        )}
                                    </Typography>

                                    <Typography variant="caption">
                                        Type :&nbsp; &nbsp;
                                        {bot.botMessageType == BotMessageType.OTP ? (
                                            <b>{BotMessageType.OTP}</b>
                                        ) : bot.botMessageType == BotMessageType.Promotional ? (
                                            <b>{BotMessageType.Promotional}</b>
                                        ) : bot.botMessageType == BotMessageType.Transactional ? (
                                            <b>{BotMessageType.Transactional}</b>
                                        ) : (
                                            "NA"
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled>
                        <Typography variant="h6">No bots found</Typography>
                    </MenuItem>
                )}
            </Select>

            {helperText && (
                <Typography color="error" variant="caption">
                    {helperText}
                </Typography>
            )}
        </FormControl>
    );
};

export default SearchableBotDropdownForTemplate;
